import { Plus, SearchLg } from '@untitledui/icons'
import { useState } from 'react'

import Button from '@components/designSystem/Button'
import { useConfirmDialog, useDialog } from '@components/designSystem/Dialog/useDialog'
import { Table, TableCard } from '@components/designSystem/Table'
import { Badge, BadgeIcon, BadgeWithButton } from '@components/designSystem/badges/badges'
import { Input } from '@components/designSystem/input/input'
import useDebounce from '@hooks/useDebounce.ts'
import useToast from '@hooks/useToast.ts'
import useTypedTranslation from '@hooks/useTypedTranslation'
import AddStoreGrantDialog from '@routes/Administration/AddStoreGrantDialog'
import { getRoleBadgeColor } from '@routes/Administration/utils'
import { useDeleteStoreGrant, type UserDTO, useUsersInfinite } from '@state/user'

function UserManagementTable() {
  const { t } = useTypedTranslation()
  const [userSearch, setUserSearch] = useState('')
  const { showToast } = useToast()
  const debouncedUserSearch = useDebounce(userSearch, 300)
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useUsersInfinite(debouncedUserSearch)
  const { mutate: deleteUserGrant } = useDeleteStoreGrant()
  const { showDialog: showAddGrantDialog } = useDialog(AddStoreGrantDialog)
  const { showDialog: removeGrantDialog } = useConfirmDialog()

  function handleGrantDeletion(userId: string, storeId: string) {
    deleteUserGrant(
      { userId, storeId },
      {
        onSuccess: () => showToast({ title: t('admin.userManagement.dialog.confirmRemove.successTitle'), variant: 'success' }),
        onError: () => showToast({ title: t('admin.userManagement.dialog.confirmRemove.errorTitle'), variant: 'error' }),
      }
    )
  }

  const pages = data?.pages ?? []
  const userList = pages.flatMap(p => p.items)
  const total = pages[0]?.pagination.total ?? 0

  function handleOpenAssignmentDialog(user: UserDTO) {
    showAddGrantDialog({ userId: user.id, userFullname: user.fullname ?? undefined, alreadyAssignedStoreIds: user.storeGrants.map(g => g.store.id) })
  }

  return (
    <div className='mt-6 flex flex-col gap-4'>
      <TableCard>
        <TableCard.Header
          title={t('admin.userManagement.table.teamMembers')}
          badge={t('admin.userManagement.table.usersCount', { count: total })}
          contentTrailing={
            <Input
              leadingIcon={{ icon: SearchLg }}
              className='w-80'
              placeholder={t('admin.userManagement.table.searchPlaceholder')}
              value={userSearch}
              onChange={setUserSearch}
            />
          }
        />
        <Table
          aria-label="User management table"
          selectionMode="none"
        >
          <Table.Header>
            <Table.Head isRowHeader label={t('admin.userManagement.table.name')} />
            <Table.Head label={t('admin.userManagement.table.email')} />
            <Table.Head label={t('admin.userManagement.table.role')} />
            <Table.Head label={t('admin.userManagement.table.assignments')} />
          </Table.Header>
          <Table.Body
            items={userList}
            renderEmptyState={() => (
              <div className="py-8 text-center text-sm text-gray-500">
                {isLoading ? t('admin.userManagement.table.loadingUsers') : t('admin.userManagement.table.noUsersMatch')}
              </div>
            )}
          >
            {(user) => {
              const isAdmin = user.role === 'ADMIN'

              return (
                <Table.Row id={user.id} aria-label={user.fullname}>
                  <Table.Cell className='text-primary'>{user.fullname}</Table.Cell>
                  <Table.Cell>{user.email}</Table.Cell>
                  <Table.Cell>
                    <Badge size="sm" color={getRoleBadgeColor(user.role)}>
                      {t(`common.roles.system.${user.role}`)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-wrap gap-2">
                      {user.storeGrants.map(({ store, role }) => (
                        <BadgeWithButton
                          key={store.id}
                          size="sm"
                          color={getRoleBadgeColor(role)}
                          buttonLabel={t('admin.userManagement.table.removeAssignment', { storeName: store.name })}
                          onButtonClick={() => removeGrantDialog({
                            title: t('admin.userManagement.dialog.confirmRemove.title', { userName: user.fullname }),
                            message: t('admin.userManagement.dialog.confirmRemove.message', { userName: user.fullname, storeName: store.name }),
                            onConfirm: () => handleGrantDeletion(user.id, store.id),
                          })}
                        >
                          {t(`common.roles.store.${role}`)} @ {store.name}
                        </BadgeWithButton>
                      ))}
                      {!isAdmin &&
                            <button
                              aria-label={t('admin.userManagement.table.addAssignment')}
                              className="cursor-pointer rounded-full"
                              onClick={() => handleOpenAssignmentDialog(user)}
                            >
                              <BadgeIcon size="sm" icon={Plus} />
                            </button>
                      }
                    </div>
                    {isAdmin && <span className="text-sm text-gray-500 italic">{t('admin.userManagement.table.adminImplicitAccessHint')}</span>}
                  </Table.Cell>
                </Table.Row>
              )
            }}
          </Table.Body>
        </Table>

        {/* Footer with pagination controls */}
        <div className="flex h-10 items-center justify-end gap-3 border-t border-gray-200 bg-gray-50 px-4">
          <div className='text-xs text-gray-600'>
            {t('admin.userManagement.pagination.showing', { current: userList.length, total })}
          </div>
          <Button
            variant='outline'
            size='sm'
            onClick={() => fetchNextPage()}
            data-testid='load-more-users'
            isLoading={isFetchingNextPage}
            isDisabled={!hasNextPage}
          >
            {t('admin.userManagement.pagination.loadMore')}
          </Button>
        </div>
      </TableCard>
    </div>
  )
}

export default UserManagementTable